import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { PoolModel } from 'models/generated/Entities.GormCrm.Pool';
import { DynamicQueryModel } from 'models/generated/Models.DynamicQuery';
import { SavePoolRequestModel } from 'models/generated/Models.SavePoolRequest';
import { firstValueFrom } from 'rxjs';
import { PushService } from 'services/push.service';
import { UserService } from 'services/user.service';

@Injectable()
export class PoolService {

	private baseUrl = `${environment.gatewayUrl}/api/task-pools`;

	private _pools?: PoolModel[];
	private _userPools?: PoolModel[];

	private poolsPromise?: Promise<PoolModel[]>;
	private userPoolsPromise?: Promise<PoolModel[]>;

	constructor(private readonly http: HttpClient, private readonly userService: UserService, private readonly pushService: PushService) {
		pushService.onPoolChanges.subscribe(() => {
			this._pools = undefined;
			this.poolsPromise = undefined;
			this._userPools = undefined;
			this.userPoolsPromise = undefined;
		});
	}

	getAllPools(): Promise<PoolModel[]> {
		if (this._pools) return new Promise((resolve) => { resolve(this._pools!) });
		if (this.poolsPromise) return this.poolsPromise;
		return this.poolsPromise = firstValueFrom(this.http.get<PoolModel[]>(this.baseUrl)).then(pools => {
			this._pools = pools;
			return pools;
		});
	}

	getCurrentUserPools(): Promise<PoolModel[]> {
		if (this._userPools) return new Promise((resolve) => { resolve(this._userPools!) });
		if (this.userPoolsPromise) return this.userPoolsPromise;

		const user = this.userService.getCurrentUser();
		const teamIds = user.userTeams.map(ut => ut.team?.id!);
		const teamIdsQueryString = teamIds.map(id => `teamIds=${id}`).join('&');
		return this.userPoolsPromise = firstValueFrom(this.http.get<PoolModel[]>(`${this.baseUrl}/user/${user.id}/?${teamIdsQueryString}`)).then(userPools => {
			this._userPools = userPools;
			return userPools;
		});
	}

	getPoolById(id: string): Promise<PoolModel> {
		return firstValueFrom(this.http.get<PoolModel>(`${this.baseUrl}/${id}`));
	}

	savePool(pool: PoolModel, queryData: DynamicQueryModel): Promise<PoolModel> {
		const request = new SavePoolRequestModel();
		request.pool = pool;
		request.queryData = queryData;
		request.include(r => r.pool);
		request.include(r => r.queryData);
		return firstValueFrom(this.http.post<PoolModel>(this.baseUrl, request));
	}

	deletePool(pool: PoolModel) : Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/${pool.id}`));
	}
}
